import React, { useState } from "react";
import {
  Card,
  Spinner,
  Alert,
  Image,
  Badge,
  Button,
  Container,
  Dropdown,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import useReview from "hooks/reviews/useReview";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const statusMap = {
  ACTIVE: "success",
  DELETED: "danger",
  DEACTIVATED: "warning",
  HIDE: "secondary",
};

const StarRating = ({ rating }) => {
  if (!rating) return <small className="text-muted">No rating</small>;
  const stars = Array.from({ length: 5 }, (_, i) =>
    i < Math.floor(rating) ? "★" : "☆"
  );
  return <span style={{ color: "#ffc107", fontSize: "1.1rem" }}>{stars.join("")}</span>;
};

const Reviews = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const {
    reviews = [],
    loading,
    error,
    totalElements,
    deleteReview,
    updateReviewStatus,
  } = useReview(taskId);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  if (!taskId) {
    return (
      <Container className="py-4">
        <Alert variant="warning" className="m-3">
          No task ID provided in URL.
        </Alert>
      </Container>
    );
  }

  const handleDeleteClick = (id) => setConfirmDeleteId(id);
  const handleConfirmDelete = async () => {
    try {
      await deleteReview(confirmDeleteId, "SOFT");
      setConfirmDeleteId(null);
    } catch {
      setConfirmDeleteId(null);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateReviewStatus(id, status);
    } catch {}
  };

  return (
    <>
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between mb-4 pb-2"
        style={{ borderBottom: "1px solid #dee2e6" }}
      >
        <Button
          variant="link"
          className="p-0 text-dark d-flex align-items-center gap-1"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          style={{ textDecoration: "none", fontWeight: "500" }}
        >
          <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
        </Button>

        <h3
          className="flex-grow-1 text-center mb-0"
          style={{ fontWeight: 600, fontSize: 18 }}
        >
          Task Reviews
        </h3>

        <div style={{ width: 32 }} /> {/* spacer */}
      </div>

      {/* Loading/Error */}
      {loading && (
        <div className="d-flex align-items-center mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading reviews...
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error.message || "Failed to load reviews."}
        </Alert>
      )}

      {!loading && !error && reviews.length === 0 && (
        <p className="text-muted fst-italic text-center">No reviews yet for this task.</p>
      )}

      {/* Reviews List */}
      {reviews.map((review) => {
        const reviewerName = review.reviewer?.nickName || "Anonymous";
        const initials = getInitials(reviewerName);
        const statusColor = statusMap[review.status] || "secondary";

        return (
          <Card key={review.id} className="mb-3 shadow-sm rounded">
            <Card.Body>
              <Row className="align-items-center">
                <Col xs="auto" className="d-flex align-items-center mb-3 mb-sm-0">
                  {review.reviewer?.profilePhoto ? (
                    <Image
                      src={review.reviewer.profilePhoto}
                      roundedCircle
                      width={48}
                      height={48}
                      alt={reviewerName}
                      onError={(e) => (e.target.style.display = "none")}
                      style={{ objectFit: "cover" }}
                      className="me-3"
                    />
                  ) : (
                    <div
                      className="bg-secondary text-white d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        fontWeight: 700,
                        fontSize: 18,
                        userSelect: "none",
                      }}
                    >
                      {initials || "NA"}
                    </div>
                  )}

                  <div>
                    <strong>{reviewerName}</strong>
                    <br />
                    <small className="text-muted">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                </Col>

                <Col xs="auto" className="ms-auto">
                  <Badge
                    bg={statusColor}
                    className="text-uppercase"
                    style={{ fontSize: "0.75rem", fontWeight: 600, padding: "0.25em 0.5em" }}
                  >
                    {review.status}
                  </Badge>
                </Col>
              </Row>

              <div className="mt-3">
                <StarRating rating={review.rating} />
              </div>

              {review.source && (
                <Badge
                  bg="secondary"
                  className="mb-2 mt-3 d-inline-block"
                  style={{ fontWeight: 600 }}
                >
                  {review.source}
                </Badge>
              )}

              <p className="mt-2 mb-0" style={{ whiteSpace: "pre-line" }}>
                {review.description || (
                  <i className="text-muted fst-italic">No comment provided</i>
                )}
              </p>

              {/* Action buttons */}
              <div className="d-flex flex-wrap gap-2 mt-3">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    className="shadow-sm"
                  >
                    Status
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.keys(statusMap).map((s) => (
                      <Dropdown.Item
                        key={s}
                        active={review.status === s}
                        onClick={() => handleStatusChange(review.id, s)}
                      >
                        {s}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <Button
                  variant="outline-danger"
                  size="sm"
                  className="shadow-sm"
                  onClick={() => handleDeleteClick(review.id)}
                >
                  Delete
                </Button>
              </div>
            </Card.Body>
          </Card>
        );
      })}

      {/* Confirm Delete Modal */}
      <Modal show={!!confirmDeleteId} onHide={() => setConfirmDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this review?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDeleteId(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Reviews;
